import apiClient from "./apiClient";
import { dashboardService } from "./dashboardService";
import { getAllInventory } from "./inventoryService";
import {
  buildLowStockPathItemList,
  countOutOfStockPathItems,
  mapLowStockToDashboardPathRows,
  normalizeLowStockPathItemList,
  normalizeTopOOSPathItemBatches,
  readPathLowStockQuantityThreshold,
} from "../utils/pathLowStock";

const SETTINGS_BASE = "/path-settings";

const fetchLowStockFromSettingsApi = async () => {
  const response = await apiClient.get(`${SETTINGS_BASE}/low-stock-items`);
  if (response.status >= 200 && response.status < 300) {
    return normalizeLowStockPathItemList(response.data);
  }
  return null;
};

const fetchLowStockFromDashboardApi = async () => {
  const data = await dashboardService.getTopOOSPathItems();
  const batches = data?.topOOSPathItems ?? data?.topOosPathItems ?? [];
  return normalizeTopOOSPathItemBatches(batches);
};

const fetchLowStockFromInventoryCompute = async (organizationId) => {
  const threshold = readPathLowStockQuantityThreshold(organizationId);
  const inventory = await getAllInventory();
  return buildLowStockPathItemList(inventory, threshold);
};

/**
 * Fetch path items at or below the low-stock quantity threshold.
 * Tries, in order: settings API → client inventory compute → dashboard OOS API.
 */
export const fetchLowStockPathItems = async (organizationId) => {
  // 1. Backend low-stock-items (grouped, full list)
  try {
    const fromSettings = await fetchLowStockFromSettingsApi();
    if (fromSettings != null && fromSettings.length > 0) {
      return fromSettings;
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      console.warn(
        "[pathLowStock] Settings API failed, trying fallbacks:",
        error.message
      );
    }
  }

  // 2. Client-side compute from /inventory
  try {
    const fromClient = await fetchLowStockFromInventoryCompute(organizationId);
    if (fromClient.length > 0) {
      return fromClient;
    }
  } catch (error) {
    console.warn("[pathLowStock] Client inventory compute failed:", error.message);
  }

  // 3. Dashboard top-oos-path-items (may be partial top batches)
  try {
    const fromDashboard = await fetchLowStockFromDashboardApi();
    if (fromDashboard.length > 0) {
      return fromDashboard;
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      console.warn("[pathLowStock] Dashboard OOS API failed:", error.message);
    }
  }

  return [];
};

export const fetchPathLowStockDashboardSummary = async (
  organizationId,
  chartLimit = 5
) => {
  const list = await fetchLowStockPathItems(organizationId);
  const outOfStockCount = countOutOfStockPathItems(list);
  const lowStockCount = list.length;

  return {
    items: list,
    lowStockCount,
    outOfStockCount,
    alertCount: lowStockCount,
    chartRows: mapLowStockToDashboardPathRows(list, chartLimit),
  };
};

export const fetchPathDashboardInventoryStatus = async (
  organizationId,
  limit = 5
) => {
  const summary = await fetchPathLowStockDashboardSummary(organizationId, limit);
  return summary.chartRows;
};
